import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useData } from '../data/DataContext';
import { useApi } from '../api/client';
import { getDashboardStats } from '../api/dashboard';
import Card from '../components/ui/Card';
import StatCard from '../components/ui/StatCard';
import Button from '../components/ui/Button';
import RevenueChart from '../components/dashboard/RevenueChart';
import RecentPayments from '../components/dashboard/RecentPayments';
import UpcomingEvents from '../components/dashboard/UpcomingEvents';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import { formatCurrency, formatNumber } from '../utils/format';

export default function DashboardPage() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { members, events, payments, activity } = useData();
  const [serverStats, setServerStats] = useState(null);

  // Pull authoritative totals from the backend when configured. Client-side
  // compute below remains the fallback (mock mode + race during fetch).
  useEffect(() => {
    if (!useApi) return;
    let cancelled = false;
    getDashboardStats()
      .then((s) => { if (!cancelled) setServerStats(s); })
      .catch(() => { if (!cancelled) setServerStats(null); });
    return () => { cancelled = true; };
  }, []);

  const localStats = useMemo(() => {
    const active = members.filter((m) => m.status === 'active').length;
    const pilgrims = events.reduce((acc, e) => acc + (e.registered || 0), 0);
    const revenue = payments
      .filter((p) => p.status === 'paid')
      .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    const monthlyAdds = members.filter((m) => {
      const d = new Date(m.joinedAt);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const percent = members.length ? Math.round((active / members.length) * 100) : 0;
    return { total: members.length, active, pilgrims, revenue, monthlyAdds, percent };
  }, [members, events, payments]);

  const stats = useMemo(() => {
    if (!serverStats) return localStats;
    const total = serverStats.totalMembers || localStats.total;
    return {
      total,
      active: serverStats.activeMembers ?? localStats.active,
      pilgrims: serverStats.pilgrimsRegistered ?? localStats.pilgrims,
      revenue: serverStats.totalRevenue ?? localStats.revenue,
      monthlyAdds: serverStats.monthlyMemberAdds ?? localStats.monthlyAdds,
      percent: total ? Math.round(((serverStats.activeMembers ?? localStats.active) / total) * 100) : 0,
    };
  }, [serverStats, localStats]);

  const monthlyRevenue = useMemo(() => {
    const source = serverStats?.monthlyRevenue?.length
      ? serverStats.monthlyRevenue
      : (() => {
          const buckets = new Array(12).fill(0);
          payments.forEach((p) => {
            if (p.status !== 'paid') return;
            const d = new Date(p.date);
            if (Number.isNaN(d.getTime())) return;
            buckets[d.getMonth()] += Number(p.amount) || 0;
          });
          return buckets;
        })();
    const max = Math.max(...source, 1);
    return source.map((value) => Math.round((value / max) * 100));
  }, [serverStats, payments]);

  const upcomingEvents = useMemo(() => {
    const now = Date.now();
    return events
      .filter((e) => new Date(e.date).getTime() >= now && e.status !== 'cancelled')
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 3);
  }, [events]);

  const recentPayments = useMemo(
    () => [...payments].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5),
    [payments]
  );

  return (
    <div>
      <div className="stats-grid">
        <StatCard
          label={t('dashboard.total_members')}
          value={formatNumber(stats.total, locale)}
          change={t('dashboard.this_month_added', { count: stats.monthlyAdds })}
          icon="👥"
          tone="sc1"
        />
        <StatCard
          label={t('dashboard.active_members')}
          value={formatNumber(stats.active, locale)}
          change={t('dashboard.percent_of_total', { percent: stats.percent })}
          icon="✝"
          tone="sc2"
        />
        <StatCard
          label={t('dashboard.pilgrims_registered')}
          value={formatNumber(stats.pilgrims, locale)}
          change={t('dashboard.this_week_added', { count: Math.max(0, Math.floor(stats.pilgrims * 0.09)) })}
          icon="🕊️"
          tone="sc3"
        />
        <StatCard
          label={t('dashboard.total_revenue')}
          value={formatCurrency(stats.revenue, locale, '').trim()}
          change={t('dashboard.revenue_year')}
          icon="💰"
          tone="sc4"
        />
      </div>

      <div className="dash-grid">
        <div>
          <Card title={t('dashboard.monthly_revenue')} subtitle={t('dashboard.monthly_revenue_sub')}>
            <RevenueChart values={monthlyRevenue} />
          </Card>

          <Card title={t('dashboard.recent_payments')}>
            <RecentPayments payments={recentPayments} />
          </Card>
        </div>

        <div>
          <Card title={t('dashboard.quick_actions')}>
            <div className="dash-quick-actions">
              <Button variant="ghost" onClick={() => navigate('/payments?new=1')}>+ {t('dashboard.record_payment')}</Button>
              <Button variant="ghost" onClick={() => navigate('/members?new=1')}>+ {t('dashboard.new_member')}</Button>
              <Button variant="ghost" onClick={() => navigate('/events?new=1')}>+ {t('dashboard.new_event')}</Button>
              <Button variant="ghost" onClick={() => navigate('/reports')}>{t('dashboard.generate_report')}</Button>
            </div>
          </Card>

          <Card title={t('dashboard.upcoming_events')}>
            <UpcomingEvents events={upcomingEvents} />
          </Card>

          <Card title={t('dashboard.recent_activity')}>
            <ActivityFeed items={activity.slice(0, 5)} />
          </Card>
        </div>
      </div>
    </div>
  );
}
